import { FC, useEffect } from "react";
import {
  AddBoardPersonModel,
  AssignBoardRoleModel,
  BoardSection,
  CompanyNameAvailability,
  CustomBusinessAddress,
  formatMatrikkelAddress,
  IncorporationForm,
  INDUSTRY_SHORTCUTS,
  MAX_CUSTOM_INDUSTRY_LENGTH,
  matrikkelAddressId,
  matrikkelObjectTypeLabel,
} from "./incorporationDomain";
import { Box, VBox } from "@/components-v2/Box";
import { MSelect } from "@/components-v2/Select";
import { useTranslation } from "@/stores/RootStoreContext";
import { MTextInput, TextInput } from "@/components-v2/TextInput";
import { MTextArea } from "@/components-v2/TextArea";
import { observer } from "mobx-react";
import { CURRENCY_NOK } from "@/utils/currency";
import { Icon } from "@/components-v2/Icon";
import { Autocomplete } from "@/components-v2/Autocomplete";
import { Alert } from "@/components-v2/Alert";
import { MDateInput } from "@/components-v2/DateInput";
import { Button, IconButton, LinkButton } from "@/components-v2/Button";
import { Dialog } from "@/components-v2/Dialog";
import { SelectActorsDialog } from "@/dialogs/dialogComponents";
import { getRoleTypeLabel } from "@/types/enums/RoleType";
import { getFullName } from "@/utils/Roles";
import {
  dsePrekontrollErrorMessage,
  NACE_SEARCH_MIN_QUERY_LENGTH,
} from "@/dbot/common/commonDomain";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { InfoButton } from "@/components-v2/InfoButton";
import { useElementId } from "@/utils/reactHelpers";
import { IncorporationArticlesSection } from "./incorporationArticlesComponents";

function companyNameStatusIcon(status: CompanyNameAvailability) {
  if (status === "available") {
    return (
      <Box paddingX={"md"}>
        <Icon name={"check"} size={"sm"} color={"success"} />
      </Box>
    );
  }

  if (status === "unavailable") {
    return (
      <Box paddingX={"md"}>
        <Icon name={"x"} size={"sm"} color={"error"} />
      </Box>
    );
  }

  return undefined;
}

const AddBoardPersonDropdown: FC<{
  form: IncorporationForm;
  section: BoardSection;
  disabled: boolean;
}> = observer(({ form, section, disabled }) => {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      disabled={disabled}
      button={{ type: "link", label: t("add"), icon: "plus", disabled }}
      items={[
        {
          type: "item",
          label: t("decisions.electionOfCompanyBoard.boardChange.button.item.add.portfolio.title"),
          onSelect: () => form.openSelectBoardPersonDialog(section),
        },
        {
          type: "item",
          label: t("decisions.incorporation.board-add-new"),
          onSelect: () => form.openAddBoardPersonDialog(section),
        },
      ]}
    />
  );
});

const AddSignerDropdown: FC<{
  form: IncorporationForm;
  disabled: boolean;
}> = observer(({ form, disabled }) => {
  const { t } = useTranslation();
  return (
    <DropdownMenu
      disabled={disabled}
      button={{
        type: "link",
        label: t("decisions.incorporation.add-signer"),
        icon: "plus",
        disabled,
      }}
      items={[
        {
          type: "item",
          label: t("decisions.incorporation.signer-select"),
          onSelect: () => form.openSelectSignerDialog(),
        },
        {
          type: "item",
          label: t("decisions.incorporation.board-add-new"),
          onSelect: () => form.addAdditionalSigner(),
        },
      ]}
    />
  );
});

export const IncorporationView: FC<{ form: IncorporationForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  const locked = form.isLocked;
  const founderName = form.context.companies.find((c) => c.isMainCompany)?.name;
  const invoiceEmailId = useElementId();
  const notificationEmailId = useElementId();

  useEffect(() => form.init(), [form]);

  return (
    <VBox gap={"md"}>
      {founderName && <Box>{t("decisions.incorporation.intro", { companyName: founderName })}</Box>}
      {form.stiftelsesdato && (
        <Alert
          type={"success"}
          icon
          message={t("decisions.incorporation.incorporation-date-set", {
            date: { type: "date", value: form.stiftelsesdato },
          })}
        />
      )}
      {locked && !form.stiftelsesdato && (
        <Alert type={"info"} icon message={<>{t("decisions.incorporation.locked-info")}</>} />
      )}
      {form.prekontrollError && (
        <Alert
          type={form.prekontrollSystemUnavailable ? "warning" : "error"}
          icon
          message={
            <VBox gap={"sm"}>
              <Box>{t(form.prekontrollError)}</Box>
              {form.prekontrollErrors && form.prekontrollErrors.length > 0 && (
                <VBox gap={"xs"}>
                  {form.prekontrollErrors.map((e, i) => (
                    <Box key={`${e.feilid ?? "x"}-${i}`}>
                      {t(dsePrekontrollErrorMessage(e))}
                      {!e.localCode && e.dynamicReturn && (
                        <>
                          {": "}
                          <code>{e.dynamicReturn}</code>
                        </>
                      )}
                    </Box>
                  ))}
                </VBox>
              )}
            </VBox>
          }
        />
      )}
      {/*<div>*/}
      {/*  <MSelect*/}
      {/*    model={form.numberOfCompaniesModel}*/}
      {/*    label={t("decisions.incorporation.numberOfCompanies")}*/}
      {/*    disabled={locked}*/}
      {/*  />*/}
      {/*</div>*/}
      <VBox>
        <Box justifyContent={"space-between"}>
          {t("decisions.companyName.title")}
          {/*<Checkbox*/}
          {/*  label={t("common.auto")}*/}
          {/*  checked={form.isNamingAuto}*/}
          {/*  disabled={locked}*/}
          {/*  onChange={() => form.toggleNamingAuto()}*/}
          {/*/>*/}
        </Box>
        {form.companyNameModels.map((model, index) => (
          <MTextInput
            key={index}
            model={model}
            disabled={locked || (form.isNamingAuto && index > 0)}
            onBlur={() => form.onCompanyNameBlur(index)}
            addon={companyNameStatusIcon(form.getCompanyNameAvailability(index))}
            addonPosition={"right"}
          />
        ))}
      </VBox>

      {form.isCustomIndustry ? (
        <VBox>
          {t("decisions.incorporation.industry")}
          <Box alignItems={"center"} gap={"md"}>
            <Box grow>{form.selectedNace?.label}</Box>
            <LinkButton
              label={t("edit")}
              icon={"edit"}
              disabled={locked}
              onClick={form.openCustomIndustryDialog}
            />
            <IconButton
              icon={"trash"}
              tooltip={t("delete")}
              disabled={locked}
              onClick={form.clearSelectedNace}
            />
          </Box>
          <Alert
            type={"info"}
            icon
            message={t("decisions.incorporation.industry-custom-approval")}
          />
        </VBox>
      ) : (
        <>
          <Autocomplete
            hideCaret
            minQueryLength={NACE_SEARCH_MIN_QUERY_LENGTH}
            label={t("decisions.incorporation.industry")}
            placeholder={t("search")}
            disabled={locked}
            items={form.suggestedNaceResults}
            idFn={(item) => item.aktivitetBransjeKode ?? item.code}
            labelFn={(item) => item.label}
            captionFn={(item) => item.category}
            action={
              form.hasNoNaceMatches
                ? {
                    label: t("decisions.incorporation.industry-custom-option"),
                    onSelect: form.openCustomIndustryDialog,
                  }
                : undefined
            }
            renderInputValue={(item) => item.label}
            value={form.selectedNace}
            inputValue={form.industrySearchModel.inputValue}
            onInputValueChange={form.setNaceSearch}
            onSelect={form.setSelectedNace}
            onClear={form.clearSelectedNace}
            loading={form.isSearchingNace}
          />

          <Box wrap>
            {INDUSTRY_SHORTCUTS.map((shortcut) => (
              <Button
                key={shortcut.industry.label}
                variant={"basic"}
                size={"xs"}
                icon={"plus"}
                label={shortcut.label}
                disabled={locked}
                fullWidth={false}
                onClick={() => form.applyIndustryShortcut(shortcut)}
              />
            ))}
          </Box>
        </>
      )}

      <VBox>
        {t("decisions.incorporation.statutory-purpose")}
        <MTextInput model={form.statutoryPurposeModel} disabled={locked} />
      </VBox>

      <VBox>
        {t("decisions.incorporation.who-signs", { company: form.mainCompany.name })}
        <Box gap={"sm"} alignItems={"center"}>
          <MSelect model={form.signerModel} placeholder={t("choose")} fullWidth disabled={locked} />
          {form.signerModel.value && (
            <IconButton
              icon={"x"}
              tooltip={t("delete")}
              onClick={form.removePrimarySigner}
              disabled={locked}
            />
          )}
        </Box>
        {form.additionalSigners.map((signer) =>
          signer.kind === "role" ? (
            <TextInput
              key={signer.id}
              value={t("decisions.incorporation.signer-role", {
                name: getFullName(signer.role),
                role: t(getRoleTypeLabel(signer.role.role)),
                company: form.mainCompany.name,
              })}
              readOnly
              addon={
                <IconButton
                  icon={"x"}
                  tooltip={t("delete")}
                  onClick={() => form.removeAdditionalSigner(signer.id)}
                  disabled={locked}
                />
              }
              addonPosition={"right"}
            />
          ) : (
            <Box key={signer.id}>
              <MTextInput model={signer.nameModel} placeholder={t("name")} disabled={locked} />
              <MTextInput model={signer.emailModel} placeholder={t("email")} disabled={locked} />
              <MTextInput
                model={signer.socialSecurityNumberModel}
                placeholder={t(
                  "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber",
                )}
                disabled={locked}
                addon={
                  <IconButton
                    icon={"x"}
                    tooltip={t("delete")}
                    onClick={() => form.removeAdditionalSigner(signer.id)}
                    disabled={locked}
                  />
                }
                addonPosition={"right"}
              />
            </Box>
          ),
        )}
        {form.duplicateSignerError && (
          <Alert type={"error"} message={<>{t(form.duplicateSignerError)}</>} />
        )}
        <Box>
          <AddSignerDropdown form={form} disabled={locked} />
        </Box>
      </VBox>
      <hr />
      {/*<VBox>*/}
      {/*  {t("decisions.incorporation.owners")}*/}
      {/*  <MRadioButtonGroup model={form.ownersModel} horizontal disabled={locked} />*/}
      {/*</VBox>*/}

      <VBox>
        <b>{t("decisions.incorporation.share-capital")}</b>
        <Box alignItems={"end"}>
          <MTextInput
            type={"number"}
            model={form.shareNominalValueModel}
            label={t("share-nominal-value")}
            prefix={CURRENCY_NOK}
            disabled={locked}
          />
          <Box className={"w-full md:w-56 min-w-0"}>
            <MSelect
              model={form.shareClassModel}
              label={t("shareType")}
              fullWidth
              disabled={locked}
            />
          </Box>
          <MTextInput
            model={form.numberOfSharesModel}
            label={t("shareClasses.numberOfShares")}
            disabled={locked}
          />
          <TextInput
            value={form.calculatedShareCapital}
            label={t("decisions.incorporation.share-capital")}
            prefix={CURRENCY_NOK}
            disabled={true}
          />
        </Box>
      </VBox>

      <MDateInput
        model={form.deadlineForContributionModel}
        label={t("decisions.incorporation.deadline-for-contribution")}
        disabled={locked}
      />

      <VBox>
        <Autocomplete
          hideCaret
          minQueryLength={2}
          label={t("decisions.incorporation.capital-confirmer.who")}
          placeholder={t("search")}
          disabled={locked}
          items={form.suggestedCapitalConfirmerCompanies}
          value={form.selectedCapitalConfirmerCompany}
          idFn={(item) => item.organizationnumber}
          labelFn={(item) => item.name}
          inputValue={form.capitalConfirmerSearchModel.inputValue}
          onInputValueChange={form.setCapitalConfirmerCompanySearch}
          onSelect={form.setSelectedCapitalConfirmerCompany}
          onClear={form.clearSelectedCapitalConfirmerCompany}
        />
        {form.capitalConfirmerValidationError && (
          <Alert type={"error"} message={<>{t(form.capitalConfirmerValidationError)}</>} />
        )}

        {form.selectedCapitalConfirmerCompany && (
          <VBox>
            <Box alignItems={"center"}>
              {t("decisions.incorporation.capital-confirmer.representative-email", {
                capitalConfirmer: form.selectedCapitalConfirmerCompany.name,
              })}
              <InfoButton
                infoText={{
                  key: "decisions.incorporation.capital-confirmer.representative-info",
                  params: { capitalConfirmer: form.selectedCapitalConfirmerCompany.name },
                }}
              />
            </Box>
            <VBox>
              <MTextInput
                model={form.capitalConfirmerRepresentativeNameModel}
                placeholder={t("name")}
                disabled={locked}
              />
              <MTextInput
                model={form.capitalConfirmerRepresentativeEmailModel}
                label={t("email")}
                disabled={locked}
              />
            </VBox>
          </VBox>
        )}
      </VBox>
      <hr />

      <VBox>
        <b>{t("decisions.incorporation.contact-information", { company: form.newCompanyName })}</b>
        <VBox gap={0}>
          <Box alignItems={"center"}>
            <label htmlFor={notificationEmailId}>
              {t("decisions.incorporation.vedtak-notification-email")}
            </label>
            <InfoButton
              infoText={{ key: "decisions.incorporation.vedtak-notification-email.info" }}
            />
          </Box>
          <MTextInput
            id={notificationEmailId}
            model={form.notificationEmailModel}
            placeholder={t("email")}
            disabled={locked}
          />
        </VBox>
        <VBox gap={0}>
          <Box alignItems={"center"}>
            <label htmlFor={invoiceEmailId}>{t("decisions.incorporation.invoice-email")}</label>
            <InfoButton infoText={{ key: "decisions.incorporation.invoice-email.info" }} />
          </Box>
          <MTextInput
            id={invoiceEmailId}
            model={form.invoiceEmailModel}
            placeholder={t("email")}
            disabled={locked}
          />
        </VBox>
      </VBox>

      <hr />

      <VBox gap={"xl"}>
        <b>{t("decisions.incorporation.company-details")}</b>

        <Autocomplete<CustomBusinessAddress>
          hideCaret
          label={t("decisions.incorporation.address")}
          placeholder={t("search")}
          disabled={locked}
          items={form.suggestedMatrikkelAddresses}
          value={form.selectedBusinessAddress ?? null}
          inputValue={form.businessAddressModel.inputValue}
          onInputValueChange={form.setBusinessAddressSearch}
          onSelect={form.selectBusinessAddress}
          onClear={form.clearBusinessAddress}
          onInputBlur={form.onBusinessAddressBlur}
          idFn={matrikkelAddressId}
          labelFn={formatMatrikkelAddress}
          captionFn={(item) =>
            item.objectType ? t(matrikkelObjectTypeLabel(item.objectType)) : undefined
          }
          loading={form.isSearchingMatrikkelAddresses}
          minQueryLength={3}
          error={form.businessAddressModel.error !== undefined}
          message={
            form.businessAddressModel.error
              ? t(form.businessAddressModel.error)
              : form.businessAddressModel.inputValue.trim() === ""
                ? t("decisions.incorporation.address-inherited-when-empty", {
                    company: form.mainCompany.name,
                  })
                : t("decisions.incorporation.address-matrikkel-info")
          }
        />

        <VBox>
          {t("decisions.incorporation.styre")}
          {form.styreAssignments.length === 0 ? (
            <>
              <Box>{t("decisions.incorporation.chairperson-required")}</Box>
              <Box>
                <AddBoardPersonDropdown form={form} section={"board"} disabled={locked} />
              </Box>
            </>
          ) : (
            <>
              {form.styreAssignments.map((assignment) => (
                <TextInput
                  key={assignment.id}
                  value={`${assignment.name}, ${t(getRoleTypeLabel(assignment.role)).toLowerCase()}`}
                  readOnly
                  addon={
                    <IconButton
                      icon={"x"}
                      tooltip={t("delete")}
                      onClick={() => form.removeBoardAssignment(assignment.id)}
                      disabled={locked}
                    />
                  }
                  addonPosition={"right"}
                />
              ))}
              <Box>
                <AddBoardPersonDropdown form={form} section={"board"} disabled={locked} />
              </Box>
            </>
          )}
          {form.boardChairError && (
            <Alert type={"error"} message={<>{t(form.boardChairError)}</>} />
          )}
          {form.duplicateBoardPersonError && (
            <Alert type={"error"} message={<>{t(form.duplicateBoardPersonError)}</>} />
          )}
        </VBox>

        <VBox>
          {t("decisions.incorporation.daglig-leder")}
          {form.dagligLedere.length === 0 ? (
            <>
              <Box>{t("decisions.incorporation.optional-field")}</Box>
              <Box>
                <AddBoardPersonDropdown form={form} section={"ceo"} disabled={locked} />
              </Box>
            </>
          ) : (
            form.dagligLedere.map((assignment) => (
              <TextInput
                key={assignment.id}
                value={`${assignment.name}, ${t(getRoleTypeLabel(assignment.role)).toLowerCase()}`}
                readOnly
                addon={
                  <IconButton
                    icon={"x"}
                    tooltip={t("delete")}
                    onClick={() => form.removeBoardAssignment(assignment.id)}
                    disabled={locked}
                  />
                }
                addonPosition={"right"}
              />
            ))
          )}
        </VBox>

        <VBox>
          {t("decisions.incorporation.auditor")}
          {!form.auditorSelectModel.value ? (
            <>
              <Box>{t("decisions.incorporation.optional-field")}</Box>
              <Box>
                <LinkButton
                  icon={"plus"}
                  label={t("add")}
                  onClick={() => form.auditorSelectModel.setValue(true)}
                  disabled={locked}
                />
              </Box>
            </>
          ) : (
            <VBox gap={"md"}>
              <VBox>
                <Autocomplete
                  hideCaret
                  minQueryLength={2}
                  placeholder={t("search")}
                  disabled={locked}
                  items={form.suggestedAuditorCompanies}
                  value={form.selectedAuditorCompany}
                  idFn={(item) => item.organizationnumber}
                  labelFn={(item) => item.name}
                  inputValue={form.auditorSearchModel.inputValue}
                  onInputValueChange={form.setAuditorCompanySearch}
                  onSelect={form.setSelectedAuditorCompany}
                  onClear={(reason) => {
                    form.clearSelectedAuditorCompany();
                    if (reason === "clear-press") {
                      form.auditorSelectModel.setValue(false);
                    }
                  }}
                />
              </VBox>
              {form.auditorWarning && (
                <Alert type={"warning"} icon message={t(form.auditorWarning)} />
              )}
              {form.selectedAuditorCompany && (
                <>
                  <VBox gap={0}>
                    <Box alignItems={"center"}>
                      {t("decisions.incorporation.auditor-representative", {
                        auditorCompanyName: form.selectedAuditorCompany.name,
                      })}
                      <InfoButton
                        infoText={{
                          key: "decisions.incorporation.auditor-representative-info",
                          params: { auditorCompanyName: form.selectedAuditorCompany.name },
                        }}
                      />
                    </Box>
                    <VBox>
                      <MTextInput
                        model={form.auditorRepresentativeNameModel}
                        placeholder={t("name")}
                        disabled={locked}
                      />
                      <MTextInput
                        model={form.auditorEmailModel}
                        label={t("email")}
                        disabled={locked}
                      />
                    </VBox>
                  </VBox>
                  <MTextInput
                    model={form.auditorCostModel}
                    label={t("decisions.incorporation.auditor-cost")}
                    prefix={CURRENCY_NOK}
                    disabled={locked}
                  />
                </>
              )}
            </VBox>
          )}
        </VBox>

        <VBox>
          {t("decisions.incorporation.accountant")}
          {!form.accountantSelectModel.value ? (
            <>
              <Box>{t("decisions.incorporation.optional-field")}</Box>
              <Box>
                <LinkButton
                  icon={"plus"}
                  label={t("add")}
                  onClick={() => form.accountantSelectModel.setValue(true)}
                  disabled={locked}
                />
              </Box>
            </>
          ) : (
            <VBox gap={"md"}>
              <VBox>
                <Autocomplete
                  hideCaret
                  minQueryLength={2}
                  placeholder={t("search")}
                  disabled={locked}
                  items={form.suggestedAccountantCompanies}
                  value={form.selectedAccountantCompany}
                  idFn={(item) => item.organizationnumber}
                  labelFn={(item) => item.name}
                  inputValue={form.accountantSearchModel.inputValue}
                  onInputValueChange={form.setAccountantCompanySearch}
                  onSelect={form.setSelectedAccountantCompany}
                  onClear={(reason) => {
                    form.clearSelectedAccountantCompany();
                    if (reason === "clear-press") {
                      form.accountantSelectModel.setValue(false);
                    }
                  }}
                />
              </VBox>
              {form.accountantWarning && (
                <Alert type={"warning"} icon message={t(form.accountantWarning)} />
              )}
              {form.selectedAccountantCompany && (
                <VBox gap={0}>
                  <Box alignItems={"center"}>
                    {t("decisions.incorporation.accountant-representative", {
                      accountantCompanyName: form.selectedAccountantCompany.name,
                    })}
                    <InfoButton
                      infoText={{
                        key: "decisions.incorporation.accountant-representative-info",
                        params: { accountantCompanyName: form.selectedAccountantCompany.name },
                      }}
                    />
                  </Box>
                  <VBox>
                    <MTextInput
                      model={form.accountantRepresentativeNameModel}
                      placeholder={t("name")}
                      disabled={locked}
                    />
                    <MTextInput
                      model={form.accountantEmailModel}
                      label={t("email")}
                      disabled={locked}
                    />
                  </VBox>
                </VBox>
              )}
            </VBox>
          )}
        </VBox>

        {/*<VBox>*/}
        {/*  {t("decisions.incorporation.continuation")}*/}
        {/*  <MRadioButtonGroup model={form.continuationSelectModel} horizontal disabled={locked} />*/}
        {/*</VBox>*/}

        {/*<VBox>*/}
        {/*  {t("decisions.incorporation.has-employees")}*/}
        {/*  <MRadioButtonGroup model={form.hasEmployeesSelectModel} horizontal disabled={locked} />*/}
        {/*</VBox>*/}
      </VBox>

      <hr />

      <IncorporationArticlesSection form={form} />

      <hr />

      <VBox gap={"md"}>
        <b>{t("decisions.incorporation.other-info")}</b>
        <VBox gap={false}>
          {t("decisions.incorporation.info.stiftelsesdato")}
          <Box>{t("decisions.incorporation.info.stiftelsesdato-value")}</Box>
        </VBox>
        <VBox gap={false}>
          {t("decisions.incorporation.info.vedtektsdato")}
          <Box>{t("decisions.incorporation.info.vedtektsdato-value")}</Box>
        </VBox>
        <VBox gap={false}>
          {t("decisions.incorporation.info.oppstartsdato")}
          <Box>{t("decisions.incorporation.info.oppstartsdato-value")}</Box>
        </VBox>
        <VBox gap={false}>
          {t("decisions.incorporation.info.employees")}
          <Box>{t("decisions.incorporation.info.employees-value")}</Box>
        </VBox>
        <VBox gap={false}>
          {t("decisions.incorporation.info.language")}
          <Box>{t("decisions.incorporation.info.language-value")}</Box>
        </VBox>
        <VBox gap={false}>
          {t("decisions.incorporation.info.subscription")}
          <Box>{t("enterprise-account.digital-company")}</Box>
        </VBox>
        <Box className={"subtle text-sm"}>{t("decisions.incorporation.info.activation")}</Box>
      </VBox>

      <IncorporationBoardDialogs form={form} />
      <IncorporationSignerDialog form={form} />
      <CustomIndustryDialog form={form} />
    </VBox>
  );
});

const CustomIndustryDialog: FC<{ form: IncorporationForm }> = observer(({ form }) => {
  const { t } = useTranslation();
  const model = form.customIndustryModel;
  if (!model) {
    return null;
  }
  return (
    <Dialog
      title={t("decisions.incorporation.industry-custom-dialog-title")}
      maxWidth={"xs"}
      onCancel={form.closeCustomIndustryDialog}
      onSubmit={form.saveCustomIndustry}
      isSubmitDisabled={model.isEmpty}
    >
      <VBox gap={"md"}>
        <VBox gap={"sm"}>
          {t("decisions.incorporation.industry-custom-dialog-requirements")}
          <ul className={"list-disc pl-5"}>
            <li>{t("decisions.incorporation.industry-custom-dialog-requirement-products")}</li>
            <li>{t("decisions.incorporation.industry-custom-dialog-requirement-sector")}</li>
            <li>{t("decisions.incorporation.industry-custom-dialog-requirement-norwegian")}</li>
          </ul>
        </VBox>
        <MTextArea
          model={model}
          label={t("decisions.incorporation.industry-custom-dialog-description")}
          placeholder={t("decisions.incorporation.industry-custom-dialog-placeholder")}
          maxLength={MAX_CUSTOM_INDUSTRY_LENGTH}
          enforceMaxLength={false}
          onChange={(event) => form.setCustomIndustry(event.target.value)}
          autofocus
        />
      </VBox>
    </Dialog>
  );
});

const IncorporationSignerDialog: FC<{ form: IncorporationForm }> = observer(({ form }) => {
  const state = form.signerDialogState;
  return state.type === "select-person" ? <SelectActorsDialog model={state.model} /> : null;
});

const IncorporationBoardDialogs: FC<{ form: IncorporationForm }> = observer(({ form }) => {
  const state = form.boardDialogState;
  switch (state.type) {
    case "select-person":
      return <SelectActorsDialog model={state.model} />;
    case "add-person":
      return <AddBoardPersonDialog model={state.model} />;
    case "assign-role":
      return <AssignBoardRoleDialog model={state.model} />;
    default:
      return null;
  }
});

const AddBoardPersonDialog: FC<{ model: AddBoardPersonModel }> = observer(({ model }) => {
  return (
    <Dialog
      title={{ key: "decisions.incorporation.board-add-person" }}
      onCancel={model.onClose}
      onSubmit={model.submit}
      maxWidth={"sm"}
      isSubmitDisabled={!model.isValid}
    >
      <VBox gap={"md"}>
        <MTextInput
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.name" }}
          model={model.nameModel}
        />
        <MSelect
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.role" }}
          model={model.roleModel}
        />
        <MSelect
          label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.nationality" }}
          model={model.countryModel}
          onSelect={model.setCountry}
        />
        <MTextInput
          label={{
            key: "decisions.electionOfCompanyBoard.boardChange.dialog.socialSecurityNumber",
          }}
          model={model.socialSecurityNumberModel}
        />
      </VBox>
    </Dialog>
  );
});

const AssignBoardRoleDialog: FC<{ model: AssignBoardRoleModel }> = observer(({ model }) => {
  return (
    <Dialog title={model.name} onCancel={model.onClose} onSubmit={model.submit} maxWidth={"sm"}>
      <MSelect
        label={{ key: "decisions.electionOfCompanyBoard.boardChange.dialog.role" }}
        model={model.roleModel}
      />
    </Dialog>
  );
});
